import { CollapsibleContent } from '@radix-ui/react-collapsible';
import { Portal } from '@radix-ui/react-tooltip';

import { useEffect, useRef, useState } from 'react';

import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';

import { Button } from '@/components//ui/button';

import anticipateDetail from '@/utils/anticipateRender/detail';
import { markdownToComponent } from '@/utils/convertMarkdown';
import formatName from '@/utils/format/formatName';
import formatURL from '@/utils/format/formatURL';
import getCurrentIds from '@/utils/getCurrentIds';
import getIcon, { hasIcon } from '@/utils/getIcon';
import { cn } from '@/utils/lib/utils';
import { ArrowUpRight } from '@/utils/lucide';
import { BookOpen } from '@/utils/lucide';
import { Square } from '@/utils/lucide';
import { CheckCircle } from '@/utils/lucide';
import { Circle } from '@/utils/lucide';
import { RotateCw } from '@/utils/lucide';
import { AlertOctagon } from '@/utils/lucide';
import { ChevronDown } from '@/utils/lucide';
import { ChevronUp } from '@/utils/lucide';
import openDetail from '@/utils/openDetail';
import showToast from '@/utils/toast/showToast';
import { z_classes } from '@/utils/z';

import store from '@/redux/store';
import { setActiveTabIndex } from '@/redux/tabSlice';

import { StatusTreeData } from '@/types/Evidence';
import { EvidenceExplanation } from '@/types/Evidence';

import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';

const getSource = (e: any, item: any) =>
{
	const type = item.type ?? item.source_type ?? item.status_type;
	const id = item.id ?? item.source_id ?? item.status_id ?? item.entity_id;

	if (type === 'trial' || type === 'drug' || type === 'company')
	{
		const { session_id } = getCurrentIds();
		socket.emit('take_action', {
			action_type: `${type}_detail`,
			[`${type}_id`]: id,
			id,
			session_id
		});

		anticipateDetail();
	}
	else if (type === 'webpage')
	{
		window.open(item.url, '_blank');
	}
	else if (type === 'tab')
	{
		//Set the tab to the source tab
		console.log('Opening tab with ID: ', id);
		const tabIndex = store.getState().session.sessionData?.tabs?.findIndex(tab => tab.id === id);

		const activeTabIndex = store.getState().tab.activeTabIndex;

		//If the tab is already open, then don't open it again, and notify the user
		if (tabIndex === activeTabIndex)
		{
			console.log('Tab is already open');
			showToast({
				variant: 'outline',
				title: 'Tab Already Open',
				description: 'This tab is already open.'
			});
			return;
		}
		else if (tabIndex !== undefined && tabIndex > -1)
		{
			console.log('Setting active tab index:', tabIndex);
			store.dispatch(setActiveTabIndex(tabIndex));
		}
		else
		{
			console.error('Could not find tab index:', id);
			showToast({
				variant: 'error',
				title: 'Tab Closed',
				description: 'This tab has been closed and is no longer available.'
			});
			return;
		}
	}
	else if (type === 'field')
	{
		// Not sure what to do here
	}
	else
	{
		openDetail(e, id);
	}
};

const MessageStatusTree = ({
	status_tree,
	focusFilters,
	className,
	defaultOpen
}: {
	status_tree: StatusTreeData;
	focusFilters?: any[];
	className?: string;
	defaultOpen?: boolean;
}) =>
{
	const statusTreeName = status_tree.name ?? 'View Steps';
	const [open, setOpen] = useState<Record<string, boolean>>({
		[statusTreeName]: !!defaultOpen
	});
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const sections = status_tree.sections;

	const initialHandled = useRef(false);

	useEffect(() =>
	{
		if (!initialHandled.current)
		{
			initialHandled.current = true;
			return;
		}

		setOpen(prev => ({
			...prev,
			[statusTreeName]: true
		}));
	}, [status_tree]);

	// useEffect(() =>
	// {
	// 	//When the section status changes, set the open state appropriately
	// 	console.log('Updating collapse state for sections', sections);

	// 	if (!sections) return;

	// 	Object.keys(sections).forEach(sectionKey =>
	// 	{
	// 		const section = sections[sectionKey];
	// 		setOpen(prev => ({
	// 			...prev,
	// 			[formatName(section.name ?? '')]: section.status === 'complete'
	// 		}));
	// 	});
	// }, [sections]);

	return (
		<Collapsible
			className={cn(' bg-neutral-0 border rounded-md pb-0 mb-2 min-w-full', className)}
			open={!!open[statusTreeName]}
			onOpenChange={val =>
			{
				setOpen(prev => ({ ...prev, [statusTreeName]: val }));
			}}
		>
			<CollapsibleTrigger className='w-full pr-[15px] pl-3 py-2 hover:bg-neutral-100 transition-all'>
				<div className='flex flex-row items-center gap-2 text font-bold w-full '>
					<p className='flex flex-row w-full items-center gap-2'>
						<span className='text-icon text-neutral-500'>
							<BookOpen className='bg-neutral-100 aspect-square rounded-full text-neutral-900 p-[5px] w-8 h-8' />
						</span>
						{statusTreeName}
					</p>
					<span className='text-icon'>{!!open[statusTreeName] ? <ChevronUp /> : <ChevronDown />}</span>
				</div>
			</CollapsibleTrigger>
			<div
				className={cn(
					'relative flex flex-col px-2 transition-all overflow-hidden',
					open[statusTreeName] ? 'py-2 max-h-[50dvh] overflow-y-auto' : 'max-h-0'
				)}
			>
				{sections &&
					Object.keys(sections).map((sectionId, index) =>
					{
						const section = sections[sectionId];
						const Icon = getIcon(sections[sectionId].icon_type ?? 'ai_answer');

						const status = sections[sectionId].status;
						// let StatusIcon;
						// if (status === 'complete') StatusIcon = CheckCircle;
						// else StatusIcon = CheckboxOutlineBlank;

						let statusIconComponent = <></>;
						if (status === 'complete')
						{
							statusIconComponent = <CheckCircle className='text-maven-primary-500 text-icon' />;
						}
						else if (status === 'not_started')
						{
							statusIconComponent = <Circle className='text-neutral-500 text-icon' />;
						}
						else if (status === 'error' || status === 'failed' || status === 'cancelled')
						{
							statusIconComponent = <AlertOctagon className='text-maven-error-500 text-icon' />;
						}
						//else if (status === 'in_progress')
						else
						{
							statusIconComponent = <RotateCw className='text-neutral-500 animate-spin text-icon' />;
						}
						//else statusIconComponent = <Square className='text-neutral-500 text-icon' />;

						const sectionItems = section.items;
						const sectionName = formatName(section.name ?? '');

						const sectionItemsSlice = sectionItems
							? expanded[sectionName]
								? sectionItems
								: Object.keys(sectionItems)
										.slice(0, 3)
										.reduce((obj, key) => ({ ...obj, [key]: sectionItems[key] }), {})
							: {};

						//console.log('section', section, index, Object.keys(sections).length);

						const last = index < Object.keys(sections).length - 1;

						return (
							<Collapsible
								key={sectionId + index}
								open={open[sectionName]}
								onOpenChange={val =>
								{
									console.log('val', val);
									setOpen(prev => ({ ...prev, [sectionName]: val }));
								}}
								className='flex flex-col'
								defaultOpen={status === 'in_progress'}
							>
								<CollapsibleTrigger className='flex flex-row w-full items-center gap-2 hover:bg-neutral-100 rounded-md py-2 px-2 my-0.5'>
									<div className=''>{statusIconComponent}</div>
									<p className='text-nowrap text-base w-full text-start font-semibold'>{sectionName}</p>
									{(section.description || (section.items && Object.keys(section.items).length > 0)) && (
										<span className='text-icon-sm'>{!open[sectionName] ? <ChevronUp /> : <ChevronDown />}</span>
									)}
								</CollapsibleTrigger>
								<div className='flex flex-row gap-2'>
									<div className={cn('flex flex-col min-w-[24px] pl-4', last ? 'justify-center items-center' : '')}>
										{last && (
											<div
												className={cn(
													'flex min-h-[calc(100%+24px)] w-0.5 z-40',
													status === 'complete' ? 'bg-maven-primary-500/60' : 'bg-neutral-500/60'
												)}
											/>
										)}
									</div>
									<div className='flex flex-col transition-all'>
										<div className={cn('transition-all overflow-hidden', open[sectionName] ? 'max-h-0' : 'max-h-[500px]')}>
											{sectionItems && Object.keys(sectionItems).length > 0 && <div className='min-h-1' />}
											{section.description}
											<div className='flex flex-wrap gap-x-2 gap-y-1'>
												{sectionItems &&
													Object.keys(sectionItemsSlice).map((itemName, index) =>
													{
														const item = sectionItems[itemName];
														const Icon = getIcon(item.status_type ?? 'document');
														const displayIndex = item.citation_index !== undefined ? item.citation_index : index + 1;

														return (
															<Tooltip delayDuration={100} key={itemName + index}>
																<TooltipTrigger asChild>
																	<button
																		className='flex flex-row items-center text-sm gap-2 w-fit group bg-neutral-100 border rounded-full font-semibold'
																		onClick={e =>
																		{
																			getSource(e, item);
																		}}
																	>
																		<p
																			className={cn(
																				'text-black p-1 px-2 rounded-l-full',
																				item.relevant ? 'bg-maven-primary-300' : 'bg-maven-indicator-300'
																			)}
																		>
																			{displayIndex}
																		</p>
																		<p className='text-nowrap group-hover:underline overflow-hidden text-ellipsis max-w-[20ch]'>
																			{item.title}
																		</p>
																		<ArrowUpRight className='text-neutral-500 text-icon-xs group-hover:text-neutral-900' />
																	</button>
																</TooltipTrigger>
																<Portal>
																	<TooltipContent className={cn('md:max-w-[600px]', z_classes.overlay[2])} side='right'>
																		<div className='flex flex-col gap-2 p-2 rounded-md'>
																			<p className='text-neutral-900 font-semibold'>{item.title}</p>
																			{item.metadata ? (
																				<>
																					<p>
																						{item.metadata.map((meta, index) =>
																							{
																							return (
																								<span key={index} className='text-neutral-500'>
																									{formatName(meta)}
																									{index < item.metadata!.length - 1 && <span> • </span>}
																								</span>
																							);
																						})}
																					</p>
																				</>
																			) : (
																				<>
																					<p>
																						{!item.url && item.status_type && formatName(item.status_type)}
																						{item.url && <span>{formatURL(item.url)}</span>}
																					</p>
																				</>
																			)}
																			{item.notes && (
																				<div className='max-h-[25dvh] overflow-y-auto bg-neutral-50 p-2'>
																					{markdownToComponent(item.notes)}
																				</div>
																			)}
																		</div>
																	</TooltipContent>
																</Portal>
															</Tooltip>
														);
													})}
												{!!sectionItems && Object.keys(sectionItems).length > 3 && !expanded[sectionName] && (
													<button
														onClick={() =>
														{
															setExpanded(prev => ({ ...prev, [sectionName]: true }));
														}}
													>
														<p className='text-sm font-semibold text-neutral-900 px-2 py-[5px] rounded-full bg-neutral-100 hover:underline'>
															+{Object.keys(sectionItems).length - 3} more
														</p>
													</button>
												)}
											</div>
											{sectionItems && Object.keys(sectionItems).length > 0 && <div className='min-h-1' />}
										</div>
									</div>
								</div>
							</Collapsible>
						);
					})}
				{focusFilters && focusFilters.length > 1 && (
					<div className='pt-2'>
						<hr />
						<p className='font-bold pt-2 text-sm'>Focus Filters:</p>
						<div className='flex flex-wrap gap-2'>
							{focusFilters.slice(0, 5).map((filter, index) =>
							{
								const Icon = getIcon(filter.icon_key);

								return (
									<div key={index} className='flex flex-row items-center gap-x-2 bg-neutral-50 rounded-md p-1 text-neutral-900'>
										<Icon />
										<p className='text-sm font-semibold'>{filter.display_value}</p>
										<p className='text-xs'>{filter.secondary_text}</p>
									</div>
								);
							})}
							{focusFilters.length > 5 && (
								<button className='flex flex-row items-center gap-x-2 bg-neutral-50 border border-neutral-300 rounded-md p-1 text-neutral-500'>
									<p className='text-sm font-semibold'>+{focusFilters.length - 5} more</p>
								</button>
							)}
						</div>
					</div>
				)}
			</div>
		</Collapsible>
	);
};

export default MessageStatusTree;
